import { useRef, useState } from 'react';
import {
  useFriends, useInviteFriend, useAcceptFriend, useRemoveFriend,
  type FriendRow,
} from '../lib/api';
import { PageTransition, Pressable, Modal, Skeleton } from '../lib/motion';

export function FriendsPage() {
  const friends = useFriends();
  const accept = useAcceptFriend();
  const remove = useRemoveFriend();
  const invite = useInviteFriend();
  const [q, setQ] = useState('');
  const [removeTarget, setRemoveTarget] = useState<FriendRow | null>(null);
  // Keep the last target around so the confirm card doesn't blank out mid-exit.
  const lastTarget = useRef<FriendRow | null>(null);
  if (removeTarget) lastTarget.current = removeTarget;
  const shownTarget = removeTarget ?? lastTarget.current;

  const list = friends.data?.friends ?? [];
  const accepted = list.filter((f) => f.status === 'accepted');
  const incoming = list.filter((f) => f.status === 'pending' && f.direction === 'incoming');
  const outgoing = list.filter((f) => f.status === 'pending' && f.direction === 'outgoing');

  const handleInvite = async () => {
    const val = q.trim();
    if (!val) return;
    invite.reset();
    if (val.includes('@')) {
      await invite.mutateAsync({ email: val });
    } else {
      await invite.mutateAsync({ handle: val });
    }
    setQ('');
  };

  return (
    <PageTransition className="max-w-2xl space-y-5">
      <header>
        <h1 className="font-display text-2xl font-semibold text-squishy-dark tracking-tight">Friends</h1>
        <p className="text-sm text-slate-500 mt-0.5">Study together and climb the rankings.</p>
      </header>

      {/* ── Add friend ───────────────────────────────────────── */}
      <section className="bg-white border border-slate-200/80 rounded-2xl shadow-card p-5 space-y-3">
        <h2 className="font-display text-base font-semibold text-squishy-dark tracking-tight">Add a friend</h2>
        <p className="text-xs text-slate-500">Enter their name, email, or username. If they have an account, they'll receive a friend invite.</p>
        <form
          onSubmit={(e) => { e.preventDefault(); handleInvite(); }}
          className="flex gap-2"
        >
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); invite.reset(); }}
            placeholder="Name, email, or username"
            className="flex-1 min-w-0 rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm focus:outline-none focus:border-squishy-teal focus:bg-white transition-colors"
          />
          <Pressable
            type="submit"
            disabled={invite.isPending || q.trim().length < 2}
            className="bg-squishy-primary text-white px-5 min-h-[44px] rounded-xl text-sm font-semibold shadow-pop disabled:opacity-50 disabled:shadow-none"
          >Send</Pressable>
        </form>
        {invite.isSuccess && (
          <p className="text-xs text-emerald-600">
            If that user exists, they'll receive your friend invite!
          </p>
        )}
        {invite.isError && (
          <p className="text-xs text-red-500">Something went wrong. Please try again.</p>
        )}
      </section>

      {/* ── Incoming requests ────────────────────────────────── */}
      {incoming.length > 0 && (
        <section className="bg-white border border-slate-200/80 rounded-2xl shadow-card p-5">
          <SectionHeading label="Friend requests" count={incoming.length} />
          <ul className="divide-y divide-slate-100">
            {incoming.map((f) => (
              <li key={f.id} className="flex items-center gap-3 py-2.5">
                <Avatar name={formatRow(f)} s3Key={f.avatar_s3_key} />
                <div className="flex-1 min-w-0 truncate text-sm font-medium text-squishy-dark">{formatRow(f)}</div>
                <Pressable
                  haptic="success"
                  onClick={() => accept.mutate(f.id)}
                  className="shrink-0 bg-squishy-primary text-white px-4 min-h-[44px] text-xs font-semibold rounded-full shadow-pop"
                >Accept</Pressable>
                <Pressable
                  onClick={() => remove.mutate(f.id)}
                  className="shrink-0 border border-slate-200 px-4 min-h-[44px] text-xs font-medium rounded-full text-slate-500 active:bg-slate-50 transition-colors"
                >Decline</Pressable>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ── Outgoing requests ────────────────────────────────── */}
      {outgoing.length > 0 && (
        <section className="bg-white border border-slate-200/80 rounded-2xl shadow-card p-5">
          <SectionHeading label="Sent" count={outgoing.length} />
          <ul className="divide-y divide-slate-100">
            {outgoing.map((f) => (
              <li key={f.id} className="flex items-center gap-3 py-2 text-sm">
                <Avatar name={formatRow(f)} s3Key={f.avatar_s3_key} />
                <div className="flex-1 min-w-0 truncate font-medium text-squishy-dark">{formatRow(f)}</div>
                <span className="text-[11px] font-medium uppercase tracking-wide text-slate-400 shrink-0">Pending</span>
                <Pressable
                  onClick={() => remove.mutate(f.id)}
                  aria-label="Cancel request"
                  className="shrink-0 p-3 -mr-2 rounded-full text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                >
                  <TrashIcon />
                </Pressable>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ── Friends list ─────────────────────────────────────── */}
      <section className="bg-white border border-slate-200/80 rounded-2xl shadow-card p-5">
        <SectionHeading label="Your friends" count={accepted.length} />
        {friends.isLoading ? (
          <div className="space-y-2 py-1">
            <Skeleton className="h-12" />
            <Skeleton className="h-12" />
            <Skeleton className="h-12" />
          </div>
        ) : accepted.length === 0 ? (
          <div className="text-center py-8">
            <div className="mx-auto w-12 h-12 rounded-full bg-teal-50 text-squishy-tealDark flex items-center justify-center">
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-6 h-6">
                <path d="M11 5a3 3 0 11-6 0 3 3 0 016 0zM2.615 16.428a1.224 1.224 0 01-.569-1.175 6.002 6.002 0 0111.908 0c.058.467-.172.92-.57 1.174A9.953 9.953 0 018 18a9.953 9.953 0 01-5.385-1.572zM16.25 5.75a.75.75 0 00-1.5 0v2h-2a.75.75 0 000 1.5h2v2a.75.75 0 001.5 0v-2h2a.75.75 0 000-1.5h-2v-2z" />
              </svg>
            </div>
            <p className="text-sm font-semibold text-squishy-dark mt-3">No friends yet</p>
            <p className="text-xs text-slate-500 mt-1">Add a classmate above to compare progress.</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {accepted.map((f) => (
              <li key={f.id} className="flex items-center gap-3 py-2 text-sm">
                <Avatar name={formatRow(f)} s3Key={f.avatar_s3_key} />
                <div className="flex-1 min-w-0 truncate font-medium text-squishy-dark">{formatRow(f)}</div>
                <Pressable
                  onClick={() => setRemoveTarget(f)}
                  aria-label={`Remove ${formatRow(f)}`}
                  className="shrink-0 p-3 -mr-2 rounded-full text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                >
                  <TrashIcon />
                </Pressable>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ── Remove-friend confirmation ───────────────────────── */}
      <Modal open={Boolean(removeTarget)} onClose={() => { if (!remove.isPending) setRemoveTarget(null); }}>
        {shownTarget && (
          <div className="bg-white rounded-3xl p-6 shadow-pop">
            <h3 className="font-display text-lg font-semibold text-squishy-dark tracking-tight">Remove friend?</h3>
            <p className="text-sm text-slate-600 mt-2">
              Remove <span className="font-semibold">{formatRow(shownTarget)}</span> from your friends? You'll both stop appearing in each other's friends list and rankings. You can add them again later.
            </p>
            <div className="flex gap-2 mt-5">
              <Pressable
                onClick={() => setRemoveTarget(null)}
                disabled={remove.isPending}
                className="flex-1 border border-slate-200 rounded-xl min-h-[44px] text-sm font-medium text-slate-600 active:bg-slate-50 transition-colors disabled:opacity-50"
              >Cancel</Pressable>
              <Pressable
                haptic="heavy"
                onClick={() => { remove.mutate(shownTarget.id); setRemoveTarget(null); }}
                disabled={remove.isPending}
                className="flex-1 bg-red-500 text-white rounded-xl min-h-[44px] text-sm font-semibold disabled:opacity-60"
              >Remove</Pressable>
            </div>
          </div>
        )}
      </Modal>
    </PageTransition>
  );
}

function SectionHeading({ label, count }: { label: string; count: number }) {
  return (
    <div className="flex items-center gap-2 mb-2">
      <h2 className="font-display text-base font-semibold text-squishy-dark tracking-tight">{label}</h2>
      <span className="text-[11px] font-semibold text-squishy-tealDark bg-teal-50 rounded-full px-2 py-0.5 tabular-nums">{count}</span>
    </div>
  );
}

function TrashIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
      <path fillRule="evenodd" d="M8.75 1A2.75 2.75 0 006 3.75v.443c-.795.077-1.58.177-2.365.298a.75.75 0 10.23 1.482l.149-.022.841 10.518A2.75 2.75 0 007.596 19h4.807a2.75 2.75 0 002.742-2.53l.841-10.52.149.023a.75.75 0 00.23-1.482A41.03 41.03 0 0014 4.193V3.75A2.75 2.75 0 0011.25 1h-2.5zM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4zM8.58 7.72a.75.75 0 00-1.5.06l.3 7.5a.75.75 0 101.5-.06l-.3-7.5zm4.34.06a.75.75 0 10-1.5-.06l-.3 7.5a.75.75 0 101.5.06l.3-7.5z" clipRule="evenodd" />
    </svg>
  );
}

function formatRow(f: FriendRow): string {
  const real = `${f.first_name} ${f.last_name?.[0] ?? ''}.`;
  // Accepted friends always see your real name, even if you're anonymous / use a handle.
  if (f.status === 'accepted') return real;
  if (f.display_name_mode === 'custom' && f.custom_handle) return `@${f.custom_handle}`;
  if (f.display_name_mode === 'anonymous') return `Student #${f.id.slice(0, 4).toUpperCase()}`;
  return real;
}

function Avatar({ name, s3Key }: { name: string; s3Key?: string | null }) {
  return (
    <div className="w-10 h-10 shrink-0 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center text-sm font-semibold text-slate-600 overflow-hidden">
      {s3Key ? (
        <img
          src={`https://squishy-uploads-${import.meta.env.VITE_AWS_ACCOUNT}.s3.amazonaws.com/${s3Key}`}
          alt=""
          className="w-full h-full object-cover"
        />
      ) : (
        name[0]?.replace('@', '')
      )}
    </div>
  );
}
